import '../styles/main.scss';
import { ParticleBackground } from './background.js';
import { ProfileAnimations } from './animations.js';
import { createIcons, icons } from 'lucide';

/**
 * Main application initialization
 */
class App {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.start());
    } else {
      this.start();
    }
  }

  start() {
    // Initialize Lucide icons
    createIcons({ icons });

    // Initialize Three.js background
    new ParticleBackground('bg-canvas');

    // Initialize animations
    new ProfileAnimations();

    // Initialize custom cursor (desktop)
    initCustomCursor();

    console.log('🚀 Application initialized');
  }
}

// Start the application
new App();

/**
 * Custom cursor implementation.
 * - Adds a small inner dot and a larger outer ring that trails the mouse.
 * - Hides automatically on touch devices.
 */
function initCustomCursor(){
  try{
    // hide on coarse pointers (mobile/touch)
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      document.body.classList.add('no-cursor');
      return;
    }

    const outer = document.getElementById('cursor-outer');
    const inner = document.getElementById('cursor-inner');
    if (!outer || !inner) return;

    // initial visibility
    outer.classList.remove('visible');
    inner.classList.remove('visible');

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;

    let outerX = mouseX;
    let outerY = mouseY;

    const lerp = (a, b, n) => (1 - n) * a + n * b;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      outer.classList.add('visible');
      inner.classList.add('visible');
    });

    document.addEventListener('mouseenter', () => {
      outer.classList.add('visible');
      inner.classList.add('visible');
    });

    document.addEventListener('mouseleave', () => {
      outer.classList.remove('visible');
      inner.classList.remove('visible');
    });

    document.addEventListener('mousedown', () => {
      outer.classList.add('is-down');
      inner.classList.add('is-active');
    });

    document.addEventListener('mouseup', () => {
      outer.classList.remove('is-down');
      inner.classList.remove('is-active');
    });

    // Add hover behaviour for interactive elements
    const hoverable = document.querySelectorAll('a, button, .links__item, .profile__image-wrapper');
    hoverable.forEach((el) => {
      el.addEventListener('mouseenter', () => {
        outer.classList.add('is-hover');
        inner.classList.add('is-hover');
      });
      el.addEventListener('mouseleave', () => {
        outer.classList.remove('is-hover');
        inner.classList.remove('is-hover');
      });
    });

    // Animation loop: outer lags behind inner for trailing effect
    function render() {
      outerX = lerp(outerX, mouseX, 0.15);
      outerY = lerp(outerY, mouseY, 0.15);

      outer.style.left = outerX + 'px';
      outer.style.top = outerY + 'px';

      inner.style.left = mouseX + 'px';
      inner.style.top = mouseY + 'px';

      requestAnimationFrame(render);
    }

    requestAnimationFrame(render);
  }catch(err){
    // non-critical: swallow errors so main app still runs
    // console.warn('Cursor init failed', err);
  }
}
